import {useMemo} from 'react';

import {Table, TableSkeleton, TBody, Td, Th, Thead, Tr} from '@/components';
import type {BusinessRFINote} from '@/types';
import * as formatDate from '@/utils/date';

const formatAction = (note: BusinessRFINote): string => {
  const label = note.action.replace(/_/g, ' ');
  return note.documentType ? `${label}: ${note.documentType}` : label;
};

type BusinessRFINotesTableProps = {
  isLoading: boolean;
  notes?: BusinessRFINote[];
  resolveActor?: (note: BusinessRFINote) => string;
};

const BusinessRFINotesTable = ({
  isLoading,
  notes = [],
  resolveActor = (note) => note.changedBy,
}: BusinessRFINotesTableProps) => {
  // Newest first for quick scanning (notes are stored append-only / chronological).
  const ordered = useMemo(() => [...notes].reverse(), [notes]);

  const renderNotes = () => {
    if (isLoading) {
      return <TableSkeleton />;
    }

    if (!ordered.length) {
      return (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-3">
          No activity yet.
        </p>
      );
    }

    return (
      <div className="max-h-[400px] overflow-y-auto overflow-x-hidden">
        <Table>
          <Thead>
            <Tr enableHover={false}>
              <Th className="px-4 py-2 text-left">Date</Th>
              <Th className="px-4 py-2 text-left">Actor</Th>
              <Th className="px-4 py-2 text-left">Action</Th>
              <Th className="px-4 py-2 text-left">Status</Th>
              <Th className="px-4 py-2 text-left">Note</Th>
            </Tr>
          </Thead>
          <TBody>
            {ordered.map((note, idx) => (
              <Tr key={idx}>
                <Td className="px-4 py-2">
                  {note.timestamp
                    ? formatDate.DDMonYYYY(String(note.timestamp), true)
                    : '-'}
                </Td>
                <Td className="px-4 py-2">
                  {resolveActor(note)} ({note.actorType})
                </Td>
                <Td className="px-4 py-2">{formatAction(note)}</Td>
                <Td className="px-4 py-2">{note.status ?? '-'}</Td>
                <Td className="px-4 py-2">{note.note ?? '-'}</Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="w-full mt-6">
      <h4 className="font-semibold text-gray-900 dark:text-gray-300 mb-2">
        {'Request For Information Audit Logs'.toLocaleUpperCase()}
      </h4>
      <hr className="mb-3 dark:border-dark-borderMid" />

      {renderNotes()}
    </div>
  );
};

export default BusinessRFINotesTable;
